import React, { memo, useState } from 'react';
import { Avatar, Menu, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

import FullscreenLoader from 'modules/views/shared/FullscreenLoader';
import Switch from '../../Switch';
import WorkerStatusLoginModal, { WorkerStatusLoginModalType } from 'modules/views/shared/WorkerStatusLoginModal';
import HelpModal from 'modules/views/shared/HelpModal';
import { ReactComponent as ArrowDownIcon } from 'assets/icons/ArrowDown.svg';
import { ReactComponent as PersonIcon } from 'assets/icons/PersonBlue.svg';
import { ReactComponent as AdminModeIcon } from 'assets/icons/PersonGray.svg';
import { ReactComponent as MyProfileIcon } from 'assets/icons/PersonGray.svg';
import { ReactComponent as SupervisorModeIcon } from 'assets/icons/SupervisorMode.svg';
import { ReactComponent as HelpIcon } from 'assets/icons/HelpGray.svg';
import { ReactComponent as LogoutIcon } from 'assets/icons/Logout.svg';
import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';

import { actionMapForOtherLoaders } from 'constants/actions';
import { ROUTES } from 'constants/routes';
import { logoutUser } from 'modules/state-mgmt/user';
import { prettifyErrorResponse } from 'utils';
import { mustCreateWorkerPassword } from 'utils/onboarding';
import { enqueueSnackbar } from 'notistack';
import { useStylesWorkerAndAdminMenu } from '../styles';

const packageJson = require('../../../../../../package.json');

export interface IAdminMenuProps {
  styleClass?: string;
}

const AdminMenu = ({ styleClass = '' }: IAdminMenuProps) => {
  const classes = useStylesWorkerAndAdminMenu();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isLogoutLoading, isSupervisorMode, mustCreatePassword } = useAppSelector(state => ({
    isLogoutLoading: state.loader.otherLoaders?.[actionMapForOtherLoaders.logoutUser]?.isLoading,
    isSupervisorMode: state.app.isSupervisorMode,
    // While an OTP-verified worker still has to create their password, this entry is the only
    // item here that is not PIN-gated, and it opens the *admin's* own profile. Hide it so the
    // worker holding the device cannot reach it; Admin Mode, Supervisor Mode and Logout keep
    // their existing PIN prompts (CH-4515).
    mustCreatePassword: mustCreateWorkerPassword(state.onboarding),
  }));

  const [workerStatusLoginModal, setWorkerStatusLoginModal] = useState<{
    isOpen: boolean;
    type: WorkerStatusLoginModalType;
  }>({
    isOpen: false,
    type: null,
  });
  const [isHelpModalVisible, setIsHelpModalVisible] = useState(false);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClickMenuItem = e => {
    if (e.target.closest('.MuiSwitch-root') || e.target.closest('#supervisor-mode-menu-item')) {
      setWorkerStatusLoginModal({ isOpen: true, type: WorkerStatusLoginModalType.SUPERVISOR });
      return;
    }
    if (e.target.closest('#admin-mode-menu-item')) {
      if (isSupervisorMode) {
        handleClose();
        navigate(ROUTES.WORKER_STATUS.path);
        return;
      }
      setWorkerStatusLoginModal({ isOpen: true, type: WorkerStatusLoginModalType.ADMIN });
      return;
    }
  };

  const onGoToProfile = () => {
    handleClose();
    navigate(`${ROUTES.MY_PROFILE.path}?profile=admin`);
  };

  const onVerifyOtpSuccess = (type: WorkerStatusLoginModalType) => {
    if (workerStatusLoginModal.type === 'admin') {
      handleClose();
      navigate(ROUTES.WORKER_STATUS.path, { state: { pinVerified: true } });
    }
    if (type === WorkerStatusLoginModalType.LOGOUT) {
      onLogout(true);
    }
    setWorkerStatusLoginModal({ isOpen: false, type: null });
  };

  const onLogout = async (isSupervisorModeActive: boolean) => {
    try {
      if (!isSupervisorModeActive) {
        setWorkerStatusLoginModal({ isOpen: true, type: WorkerStatusLoginModalType.LOGOUT });
        return;
      }
      await dispatch(logoutUser({ sessionExpired: false })).unwrap();
      navigate(ROUTES.LOGIN.path);
    } catch (error) {
      enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
    }
  };

  return (
    <div className={`${classes.parent} ${styleClass}`}>
      <div onClick={handleClick} className={classes.avatarContainer} data-testid="admin-menu-button">
        <Avatar className={classes.avatar}>
          <PersonIcon />
        </Avatar>
        <ArrowDownIcon className={isOpen ? classes.arrowIconRotated : classes.arrowIcon} />
      </div>
      <Menu
        id="admin-account-menu"
        anchorEl={anchorEl}
        open={isOpen}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{ paper: { elevation: 0 } }}
        className={classes.menu}
        disableAutoFocusItem={true}
        disableScrollLock={true}
        onClose={handleClose}
        onClick={handleClickMenuItem}
      >
        <MenuItem id="admin-mode-menu-item" data-testid="admin-menu-admin-mode" disableRipple={true}>
          <AdminModeIcon />
          {t('header.adminMode')}
        </MenuItem>
        {!mustCreatePassword && (
          <MenuItem data-testid="admin-menu-my-profile" disableRipple={true} onClick={onGoToProfile}>
            <MyProfileIcon />
            {t('header.myProfile')}
          </MenuItem>
        )}
        <MenuItem
          id="supervisor-mode-menu-item"
          data-testid="admin-menu-supervisor-mode"
          disableRipple={true}
          className={classes.supervisorModeMenuItem}
        >
          <div className={classes.supervisorModeMenuItemContent}>
            <SupervisorModeIcon />
            {t('header.supervisorMode')}
          </div>
          <Switch checked={isSupervisorMode} />
        </MenuItem>
        <MenuItem
          className={classes.hideMenuItem}
          data-testid="admin-menu-help"
          disableRipple={true}
          onClick={() => setIsHelpModalVisible(true)}
        >
          <HelpIcon />
          {t('common.help')}
        </MenuItem>
        <MenuItem data-testid="admin-menu-logout" disableRipple={true} onClick={() => onLogout(isSupervisorMode)}>
          <LogoutIcon />
          {t('header.logout')}
        </MenuItem>
        <div className={classes.buildInfo}>
          V{packageJson.buildVersion}
          {packageJson.buildDate && ` - ${moment(packageJson.buildDate).format('MMMM D,YYYY')}`}
        </div>
      </Menu>
      {isLogoutLoading && <FullscreenLoader transparent={true} text={t('header.loggingOut')} />}
      {workerStatusLoginModal.isOpen && (
        <WorkerStatusLoginModal
          type={workerStatusLoginModal.type}
          onClose={() => setWorkerStatusLoginModal({ isOpen: false, type: null })}
          onSuccess={onVerifyOtpSuccess}
        />
      )}
      <HelpModal isVisible={isHelpModalVisible} onClose={() => setIsHelpModalVisible(false)} />
    </div>
  );
};

export default memo(AdminMenu);
